/**
 * Compresses an array of MongoDB ObjectIDs into a URL-safe string
 * @param {string[]} objectIds - Array of MongoDB ObjectID strings (24-char hex)
 * @return {string} URL-safe compressed string
 */
export function compressObjectIds(objectIds: string[]) {
  // Concatenate all hex strings
  const hexString = objectIds.join("");

  // Convert hex string to Uint8Array
  const uint8Array = new Uint8Array(hexString.length / 2);
  for (let i = 0; i < hexString.length; i += 2) {
    uint8Array[i / 2] = parseInt(hexString.substring(i, i + 2), 16);
  }

  // Convert to base64 safely handling large arrays
  let binaryString = "";
  uint8Array.forEach((byte) => {
    binaryString += String.fromCharCode(byte);
  });

  const base64 = btoa(binaryString);

  // Make URL-safe
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Decompresses a URL-safe string back to an array of MongoDB ObjectIDs
 * @param {string} compressed - URL-safe compressed string
 * @return {string[]} Array of MongoDB ObjectID strings
 */
export function decompressObjectIds(compressed: string) {
  // Restore proper base64: replace '-' with '+', '_' with '/'
  let base64 = compressed.replace(/-/g, "+").replace(/_/g, "/");

  // Add padding if needed
  while (base64.length % 4) {
    base64 += "=";
  }

  // Convert base64 to binary string
  const binaryString = atob(base64);

  // Convert binary string to Uint8Array
  const uint8Array = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    uint8Array[i] = binaryString.charCodeAt(i);
  }

  // Convert Uint8Array to hex string
  let hexString = "";
  for (let i = 0; i < uint8Array.length; i++) {
    const hex = uint8Array[i].toString(16).padStart(2, "0");
    hexString += hex;
  }

  // Split into ObjectIDs (24 chars each)
  const objectIds: string[] = [];
  for (let i = 0; i < hexString.length; i += 24) {
    if (i + 24 <= hexString.length) {
      objectIds.push(hexString.substring(i, i + 24));
    }
  }

  return objectIds;
}
